import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Wallet, Key, Copy, Eye, EyeOff,
  AlertTriangle, Check, X, RefreshCw,
  Shield, ArrowDownToLine, Zap
} from 'lucide-react';
import { toast } from 'sonner';

// Generate a simple player ID (8 chars)
const generatePlayerId = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let id = '';
  for (let i = 0; i < 8; i++) {
    id += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return id;
};

// Generate a local wallet
const generateLocalWallet = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  const privateKey = Array.from(array).map(b => b.toString(16).padStart(2, '0')).join('');
  const playerId = generatePlayerId();
  
  return {
    playerId,
    privateKey,
    publicKey: playerId,
    createdAt: Date.now()
  };
};

const SolanaWalletManager = ({ isOpen, onClose, userId }) => {
  // Wallet State
  const [wallet, setWallet] = useState(null);
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // UI State
  const [activeTab, setActiveTab] = useState('main');
  const [hasCopiedKey, setHasCopiedKey] = useState(false);
  
  // Load wallet from localStorage on mount
  useEffect(() => {
    const storedWallet = localStorage.getItem('burner_wallet');
    if (storedWallet) {
      try {
        const parsed = JSON.parse(storedWallet);
        setWallet(parsed);
      } catch (e) {
        console.error('Failed to load wallet:', e);
      }
    }
  }, [userId]);

  // Generate new wallet
  const generateNewWallet = async () => {
    setIsLoading(true);
    try {
      await new Promise(r => setTimeout(r, 800)); // UX delay
      
      const newWallet = generateLocalWallet();
      setWallet(newWallet);
      
      // Store in localStorage
      localStorage.setItem('burner_wallet', JSON.stringify(newWallet));
      localStorage.setItem('player_id', newWallet.playerId);
      localStorage.setItem('wallet_created', 'true');
      
      setActiveTab('main');
      setShowPrivateKey(true);
      toast.success('Wallet created!', { description: 'Save your secret key safely!' });
      
    } catch (e) {
      console.error('Failed to generate wallet:', e);
      toast.error('Wallet creation failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Delete wallet
  const deleteWallet = () => {
    if (window.confirm('Really delete wallet? Make sure you saved your secret key!')) {
      localStorage.removeItem('burner_wallet');
      localStorage.removeItem('player_id');
      setWallet(null);
      toast.success('Wallet deleted');
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
    if (label === 'Secret Key') {
      setHasCopiedKey(true);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div 
        className="fixed inset-0 bg-black/85 z-[9999] flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="bg-[#0d0d0d] border-2 border-green-500/50 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto relative"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* FIX: RED X CLOSE BUTTON */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-2 bg-red-500/20 hover:bg-red-500/40 rounded-full transition-colors z-20"
            data-testid="wallet-manager-close-x"
          >
            <X className="w-6 h-6 text-red-400" />
          </button>

          {/* Header */}
          <div className="flex items-center gap-2 p-4 border-b border-white/10 pr-14">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="font-bold text-white text-lg">LOCAL WALLET</span>
              <p className="text-white/50 text-xs">Your Player Account</p>
            </div>
          </div>

          {/* Content */}
          <div className="p-4">
            {/* No Wallet - Show Create Option */}
            {!wallet && activeTab === 'main' && (
              <div className="space-y-4">
                <div className="text-center py-8">
                  <Shield className="w-16 h-16 text-green-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">No Wallet Found</h3>
                  <p className="text-white/60 text-sm">Create a new player wallet to start</p>
                </div>
                
                <button
                  onClick={() => setActiveTab('create')}
                  className="w-full py-4 bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl font-bold text-white hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                  data-testid="create-new-wallet-btn"
                >
                  <Key className="w-5 h-5" />
                  CREATE NEW WALLET
                </button>
              </div>
            )}

            {/* Create Wallet Flow */}
            {activeTab === 'create' && (
              <div className="space-y-4">
                <button 
                  onClick={() => setActiveTab('main')}
                  className="text-white/60 hover:text-white text-sm flex items-center gap-1"
                >
                  ← Back
                </button>
                
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-bold text-yellow-400 mb-1">IMPORTANT!</h4>
                      <p className="text-yellow-200/80 text-sm">
                        After creating, you'll receive a secret key. 
                        This is the ONLY way to recover your wallet. 
                        Save it safely and NEVER share it!
                      </p>
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={generateNewWallet}
                  disabled={isLoading}
                  className="w-full py-4 bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl font-bold text-white hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="generate-wallet-btn"
                >
                  {isLoading ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    <Key className="w-5 h-5" />
                  )}
                  {isLoading ? 'CREATING...' : 'GENERATE WALLET'}
                </button>
              </div>
            )}

            {/* Main Wallet View */}
            {wallet && activeTab === 'main' && (
              <div className="space-y-4">
                {/* Player ID Card */}
                <div className="bg-gradient-to-br from-green-900/50 to-emerald-900/50 rounded-xl p-4 border border-green-500/30">
                  <div className="text-white/60 text-sm mb-1">Your Player ID</div>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-green-400">{wallet.playerId}</span>
                    <button
                      onClick={() => copyToClipboard(wallet.playerId, 'Player ID')}
                      className="p-1.5 hover:bg-white/10 rounded transition-colors"
                    >
                      <Copy className="w-4 h-4 text-white/60" />
                    </button>
                  </div>
                </div>

                {/* Receive Instructions */}
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-blue-400 font-bold mb-2">
                    <ArrowDownToLine className="w-5 h-5" />
                    To Receive $SLAP
                  </div>
                  <p className="text-white/60 text-sm">
                    Share your Player ID with others. They can send you $SLAP tokens directly.
                  </p>
                </div>

                {/* Secret Key Section */}
                <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-red-400 text-sm font-bold flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4" /> Secret Key
                    </span>
                    <button
                      onClick={() => setShowPrivateKey(!showPrivateKey)}
                      className="p-1 hover:bg-white/10 rounded transition-colors"
                    >
                      {showPrivateKey ? <EyeOff className="w-4 h-4 text-white/60" /> : <Eye className="w-4 h-4 text-white/60" />}
                    </button>
                  </div>
                  {showPrivateKey ? (
                    <div className="space-y-2">
                      <code className="text-white text-xs block break-all bg-black/30 p-2 rounded">
                        {wallet.privateKey}
                      </code>
                      <button
                        onClick={() => copyToClipboard(wallet.privateKey, 'Secret Key')}
                        className={`w-full py-2 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-colors ${
                          hasCopiedKey 
                            ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                            : 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30'
                        }`}
                      >
                        {hasCopiedKey ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        {hasCopiedKey ? 'COPIED!' : 'COPY SECRET KEY'}
                      </button>
                    </div>
                  ) : (
                    <p className="text-white/40 text-xs">Click the eye icon to reveal your secret key</p>
                  )}
                </div>

                {/* Warning */}
                <div className="flex items-start gap-2 text-xs text-red-400/70">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>NEVER share your secret key! Anyone with this key can access your wallet.</span>
                </div>

                {/* Delete Wallet */}
                <button
                  onClick={deleteWallet}
                  className="w-full py-2 bg-white/5 border border-white/10 rounded-xl text-white/40 text-sm hover:bg-red-500/10 hover:border-red-500/30 hover:text-red-400 transition-colors flex items-center justify-center gap-2"
                  data-testid="delete-wallet-btn"
                >
                  <X className="w-4 h-4" /> Delete Wallet
                </button>
              </div>
            )}
          </div>

          {/* FIX: BOTTOM CLOSE BUTTON */}
          <div className="p-4 border-t border-white/10">
            <button
              onClick={onClose}
              className="w-full py-3 bg-red-500/20 hover:bg-red-500/30 border border-red-500/50 rounded-xl font-bold text-red-400 transition-colors flex items-center justify-center gap-2"
              data-testid="wallet-manager-close-bottom"
            >
              <X className="w-5 h-5" />
              CLOSE
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default SolanaWalletManager;
